import React from "react";
import { Link } from "react-router-dom";
import { Check, X, ArrowRight, FileText, MessageCircle } from "lucide-react";
import { getPlanPrice, getRegionalPlan } from "@/data/content";
import { useRegion } from "@/hooks/useRegion";

export default function PlanCard({ plan }) {
  const { region, link } = useRegion();
  const displayPlan = getRegionalPlan(plan, region);
  const { price, period } = getPlanPrice(plan.id, region);
  const detailUrl = link(`/plans/${plan.slug}`);
  const startUrl = link(`/checkout?plan=${plan.slug}&market=${region}`);
  const inquireUrl = link(`/contact?plan=${plan.slug}`);

  return (
    <div
      data-testid={`plan-card-${plan.id}`}
      className={`relative flex flex-col rounded-2xl bg-white transition-all hover:-translate-y-1 ${
        plan.highlight
          ? "border-2 border-[#10B981] shadow-lg shadow-[#10B981]/10"
          : "border border-[#E2E8F0] shadow-sm hover:shadow-md"
      }`}
    >
      {plan.highlight && (
        <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-[#10B981] px-4 py-1 text-xs font-semibold text-white shadow-sm whitespace-nowrap z-10">
          ⭐ {plan.badge}
        </span>
      )}

      <div className="p-7 flex flex-col h-full">
        <Link
          to={detailUrl}
          data-testid={`plan-image-${plan.id}`}
          className="block rounded-xl overflow-hidden mb-5 bg-white border border-[#E2E8F0] aspect-[3/4] group"
          aria-label={`View details for ${plan.name}`}
        >
          <img
            src={plan.image}
            alt={plan.name}
            className="w-full h-full object-contain transition-transform duration-300 group-hover:scale-[1.03]"
            loading="lazy"
          />
        </Link>

        <span className="text-xs font-semibold uppercase tracking-wide text-[#94A3B8]">{plan.type}</span>
        <Link to={detailUrl} data-testid={`plan-title-${plan.id}`} className="mt-1">
          <h3 className="text-xl font-bold font-display text-[#0F172A] hover:text-[#10B981] transition-colors">
            {plan.name}
          </h3>
        </Link>

        <div className="mt-4 flex items-baseline gap-1.5">
          <span className="text-3xl font-bold font-display text-[#0F172A]">{price}</span>
          <span className="text-sm text-[#475569]">{period}</span>
        </div>
        <span className="text-xs text-[#94A3B8] mt-0.5">{plan.note}</span>

        <p className="mt-4 text-sm text-[#475569] leading-relaxed">{displayPlan.summary}</p>

        <div className="mt-6 space-y-2.5">
          {displayPlan.included.map((item) => (
            <div key={item} className="flex items-start gap-2.5">
              <Check size={17} className="text-[#10B981] mt-0.5 shrink-0" />
              <span className="text-sm text-[#1E293B]">{item}</span>
            </div>
          ))}
        </div>

        {displayPlan.excluded?.length > 0 && (
          <div className="mt-4 space-y-2.5 pt-4 border-t border-[#E2E8F0]">
            {displayPlan.excluded.map((item) => (
              <div key={item} className="flex items-start gap-2.5">
                <X size={17} className="text-[#CBD5E1] mt-0.5 shrink-0" />
                <span className="text-sm text-[#94A3B8]">{item}</span>
              </div>
            ))}
          </div>
        )}

        {plan.addOns?.length > 0 && (
          <div className="mt-4 space-y-3 pt-4 border-t border-dashed border-[#E2E8F0]">
            {plan.addOns.map((addOn) => {
              const addOnPrice = getPlanPrice(addOn.id, region);
              return (
                <div
                  key={addOn.id}
                  data-testid={`plan-addon-${addOn.id}`}
                  className="rounded-xl border border-dashed border-[#10B981]/40 bg-[#10B981]/5 p-4"
                >
                  <p className="text-xs font-semibold uppercase tracking-wide text-[#10B981]">Optional add-on</p>
                  <p className="mt-1 text-sm font-bold text-[#0F172A]">
                    {addOn.name} <span className="font-normal text-[#475569]">+{addOnPrice.price} {addOnPrice.period}</span>
                  </p>
                  <p className="mt-1 text-xs text-[#475569] leading-relaxed">{addOn.summary}</p>
                </div>
              );
            })}
          </div>
        )}

        <div className="mt-6 rounded-xl bg-[#F8FAFC] p-4">
          <p className="text-sm font-medium text-[#0F172A] italic">{displayPlan.positioning}</p>
          <p className="text-xs text-[#475569] mt-2">
            <span className="font-semibold">Best for:</span> {displayPlan.best}
          </p>
        </div>

        {displayPlan.footnote && <p className="mt-3 text-xs text-[#94A3B8]">{displayPlan.footnote}</p>}

        <div className="mt-auto pt-6 space-y-3">
          <Link
            to={startUrl}
            data-testid={`plan-cta-${plan.id}`}
            className={`group w-full inline-flex items-center justify-center gap-2 rounded-xl px-5 py-3 text-sm font-semibold transition-all ${
              plan.highlight
                ? "bg-[#10B981] text-white hover:bg-[#059669] shadow-sm"
                : "bg-[#10B981] text-white hover:bg-[#059669] shadow-sm"
            }`}
          >
            Start Your Plan
            <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
          </Link>
          <Link
            to={inquireUrl}
            data-testid={`plan-inquire-${plan.id}`}
            className="w-full inline-flex items-center justify-center gap-2 rounded-xl border border-[#10B981] px-5 py-3 text-sm font-semibold text-[#047857] hover:bg-[#ECFDF5] transition-colors"
          >
            <MessageCircle size={15} /> Inquire
          </Link>
          <Link
            to={detailUrl}
            data-testid={`plan-details-${plan.id}`}
            className="w-full inline-flex items-center justify-center gap-2 rounded-xl border-2 border-[#E2E8F0] px-5 py-3 text-sm font-semibold text-[#0F172A] hover:border-[#0F172A] transition-colors"
          >
            <FileText size={15} /> View Details
          </Link>
        </div>
      </div>
    </div>
  );
}
